// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/** @type {HTMLElement | null} */
let activeTrapContainer = null;

/** @type {HTMLElement[]} */
let activeInertedElements = [];

const FOCUSABLE_TARGET_SELECTOR =
  "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])";

/**
 * Remove inert from elements marked while a trap was active.
 */
function clearActiveInert() {
  activeInertedElements.forEach((el) => el.removeAttribute('inert'));
  activeInertedElements = [];
}

/**
 * Keep keyboard focus out of page chrome behind an overlay, including when the
 * dialog is nested (e.g. export modal inside cart-table).
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
export function inertBackgroundExcept(container) {
  /** @type {HTMLElement[]} */
  const inerted = [];

  for (const node of document.body.children) {
    if (!(node instanceof HTMLElement)) continue;
    if (node === container || node.contains(container)) continue;
    node.setAttribute('inert', '');
    inerted.push(node);
  }

  let current = container;
  while (current.parentElement && current.parentElement !== document.body) {
    const parent = current.parentElement;
    for (const sibling of parent.children) {
      if (sibling === current || !(sibling instanceof HTMLElement)) continue;
      sibling.setAttribute('inert', '');
      inerted.push(sibling);
    }
    current = parent;
  }

  return inerted;
}

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  // Use :not([disabled]) instead of :enabled so focusables match reliably across browsers
  // when the dialog was recently unhidden.
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe"
    )
  );
}

/**
 * Open custom-select listbox options (skip offsetParent/display heuristics that
 * exclude items inside the absolutely positioned menu).
 * @param {Element} el
 * @returns {boolean}
 */
function isFocusableOpenCustomSelectOption(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (!el.classList.contains('rs-custom-select__option')) return false;
  if (el.classList.contains('rs-custom-select__disabled')) return false;
  const menu = el.closest('.rs-custom-select__menu');
  const select = el.closest('.rs-custom-select');
  if (!menu || !select?.classList.contains('open')) return false;
  if (menu.hasAttribute('hidden')) return false;
  return true;
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getOpenCustomSelectTabbableOptions(container) {
  return Array.from(
    container.querySelectorAll(
      '.rs-custom-select.open .rs-custom-select__option:not(.rs-custom-select__disabled)'
    )
  ).filter((el) => isFocusableOpenCustomSelectOption(el));
}

/**
 * @param {HTMLElement} openSelect
 * @param {HTMLElement} activeOption
 */
function syncCustomSelectActiveOption(openSelect, activeOption) {
  openSelect.querySelectorAll('.rs-custom-select__option').forEach((opt) => {
    if (!(opt instanceof HTMLElement)) return;
    const isActive = opt === activeOption;
    opt.classList.toggle('is-active', isActive);
    opt.setAttribute(
      'tabindex',
      openSelect.classList.contains('open') && !opt.classList.contains('rs-custom-select__disabled')
        ? '0'
        : '-1'
    );
    if (isActive) {
      opt.setAttribute('aria-selected', 'true');
    } else if (!opt.classList.contains('is-selected')) {
      opt.setAttribute('aria-selected', 'false');
    }
  });
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function collectFocusCandidates(container) {
  /** @type {Set<HTMLElement>} */
  const seen = new Set();
  /** @type {HTMLElement[]} */
  const candidates = [];
  const add = (el) => {
    if (!(el instanceof HTMLElement) || seen.has(el)) return;
    seen.add(el);
    candidates.push(el);
  };

  getFocusableElements(container).forEach((el) => {
    if (el.classList.contains('rs-custom-select__disabled')) return;
    add(el);
  });
  getOpenCustomSelectTabbableOptions(container).forEach(add);
  return candidates;
}

/**
 * @param {HTMLElement[]} elements
 * @returns {HTMLElement[]}
 */
function sortElementsInDocumentOrder(elements) {
  return [...elements].sort((a, b) => {
    const pos = a.compareDocumentPosition(b);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/**
 * Visible focusables only (skip display:none/visibility:hidden/hidden wrappers).
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getVisibleFocusableElements(container) {
  return sortElementsInDocumentOrder(
    collectFocusCandidates(container).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.hasAttribute('disabled')) return false;
    if (el.classList.contains('rs-custom-select__disabled')) return false;
    if (isFocusableOpenCustomSelectOption(el)) return true;
    if (el.closest('.rs-custom-select__menu') && !el.closest('.rs-custom-select.open')) return false;
    if (el.closest('[hidden]')) return false;
    // Dialogs inside an aria-hidden region (e.g. main) must still expose controls to the trap.
    const obscured = el.closest('.rs-hidden, [aria-hidden="true"]');
    if (obscured) {
      const trapShown =
        container.getAttribute('aria-hidden') !== 'true' && !container.classList.contains('rs-hidden');
      const outerObscurerWrapsTrap = obscured.contains(container);
      if (!(trapShown && container.contains(el) && outerObscurerWrapsTrap)) return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (
      el.offsetParent === null &&
      style.position !== 'fixed' &&
      style.position !== 'absolute'
    ) {
      return false;
    }
    return true;
  })
  );
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getMergedTrapFocusables(container) {
  const visible = getVisibleFocusableElements(container);
  if (visible.length) return visible;
  return getTrapFocusableElements(container);
}

/**
 * @param {Node} target
 * @param {HTMLElement} container
 */
function mousedownTargetIsFocusable(target, container) {
  if (!(target instanceof Element)) return false;
  if (target.closest('label[for]')) return true;
  const focusable = target.closest(FOCUSABLE_TARGET_SELECTOR);
  return focusable instanceof HTMLElement && container.contains(focusable);
}

/**
 * Raw focusables (minimal filtering) when visibility heuristics return none.
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getTrapFocusableElements(container) {
  /** @param {Element} el */
  const basicFilter = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.hasAttribute('disabled')) return false;
    return !el.closest('[hidden]');
  };

  let list = getFocusableElements(container).filter(basicFilter);
  if (!list.length) {
    list = Array.from(container.querySelectorAll('button, a[href], input, select, textarea')).filter(
      (el) =>
        basicFilter(el) &&
        !(el instanceof HTMLInputElement && el.type === 'hidden')
    );
  }
  return list;
}

/**
 * @param {HTMLElement[]} focusable
 * @param {Element | null} activeEl
 */
function activeFocusableIndex(focusable, activeEl) {
  if (!(activeEl instanceof Node)) return -1;
  let idx = focusable.findIndex((el) => el === activeEl);
  if (idx !== -1) return idx;
  return focusable.findIndex((el) => el.contains(activeEl));
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 * @param {{ initialFocus?: HTMLElement | null, inert?: boolean }} [options]
 */
export function trapFocus(container, options = {}) {
  removeTrapFocus();

  const initialFocusEl =
    options.initialFocus instanceof HTMLElement && container.contains(options.initialFocus)
      ? options.initialFocus
      : null;

  if (options.inert) {
    activeInertedElements = inertBackgroundExcept(container);
  }

  const getBounds = () => {
    let focusable = getMergedTrapFocusables(container);
    // Include explicit initialFocus when heuristics omit it (Tab handler needs a non-empty list).
    if (!focusable.length && initialFocusEl) {
      focusable = [initialFocusEl];
    } else if (initialFocusEl && !focusable.includes(initialFocusEl)) {
      focusable = [initialFocusEl, ...focusable];
    }
    if (!focusable.length) return { focusable, first: null, last: null };
    return { focusable, first: focusable[0] ?? null, last: focusable[focusable.length - 1] ?? null };
  };

  /** Keep focus when clicking non-interactive modal chrome (prevents body focus + tab escape). */
  trapFocusHandlers.mousedown = (event) => {
    if (!(event.target instanceof Node) || !container.contains(event.target)) return;
    if (mousedownTargetIsFocusable(event.target, container)) return;
    event.preventDefault();
  };

  trapFocusHandlers.pointerdown = trapFocusHandlers.mousedown;

  /** Fully manage Tab so the browser cannot walk global tab order (escapes overlays). */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    event.stopPropagation();

    const { focusable, first, last } = getBounds();
    const fallback = initialFocusEl || first || container;

    if (!focusable.length || !first) {
      fallback?.focus({ preventScroll: true });
      return;
    }

    const activeEl = document.activeElement;
    let idx = activeFocusableIndex(focusable, activeEl);

    if (idx === -1 || !container.contains(activeEl)) {
      (event.shiftKey ? last || first : first).focus();
      return;
    }

    const len = focusable.length;
    if (event.shiftKey) {
      focusable[idx === 0 ? len - 1 : idx - 1]?.focus();
    } else {
      focusable[idx === len - 1 ? 0 : idx + 1]?.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    const { first } = getBounds();
    const fallback = initialFocusEl || first || container;
    if (!fallback) return;
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      fallback.focus({ preventScroll: true });
    }
  };

  activeTrapContainer = container;
  container.addEventListener('mousedown', trapFocusHandlers.mousedown);
  container.addEventListener('pointerdown', trapFocusHandlers.pointerdown);
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  const { first } = getBounds();
  const preferred = initialFocusEl;
  (preferred || first || container).focus({ preventScroll: true });
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus() {
  if (activeTrapContainer && trapFocusHandlers.mousedown) {
    activeTrapContainer.removeEventListener('mousedown', trapFocusHandlers.mousedown);
  }
  if (activeTrapContainer && trapFocusHandlers.pointerdown) {
    activeTrapContainer.removeEventListener('pointerdown', trapFocusHandlers.pointerdown);
  }
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
  clearActiveInert();
  activeTrapContainer = null;
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}

/** @type {{ modal: HTMLElement, initialFocusEl: HTMLElement | null, lastFocused: HTMLElement | null, modalContent: HTMLElement | null } | null} */
let exportModalTrapState = null;

/** @type {Record<string, (event: Event) => void>} */
const exportModalTrapHandlers = {};

/** @type {HTMLElement[]} */
let exportModalInertedElements = [];

/**
 * @param {HTMLElement} modal
 * @returns {HTMLElement[]}
 */
function getExportModalFocusables(modal) {
  return getMergedTrapFocusables(modal);
}

/**
 * @param {HTMLElement} openSelect
 * @param {HTMLElement[]} tabbable
 * @param {HTMLElement} currentOption
 * @param {boolean} backward
 * @returns {HTMLElement | null}
 */
function getAdjacentTabbableCustomSelectOption(openSelect, tabbable, currentOption, backward) {
  const menu = openSelect.querySelector('.rs-custom-select__menu');
  if (!menu) return null;

  const allOptions = Array.from(menu.querySelectorAll('.rs-custom-select__option'));
  const currentIdx = allOptions.indexOf(currentOption);
  if (currentIdx === -1) return null;

  if (backward) {
    for (let i = currentIdx - 1; i >= 0; i--) {
      const opt = allOptions[i];
      if (opt instanceof HTMLElement && tabbable.includes(opt)) return opt;
    }
    return null;
  }

  for (let i = currentIdx + 1; i < allOptions.length; i++) {
    const opt = allOptions[i];
    if (opt instanceof HTMLElement && tabbable.includes(opt)) return opt;
  }
  return null;
}

/**
 * Tab between options in an open custom select before the modal trap advances.
 * @param {HTMLElement} modal
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
function handleOpenCustomSelectTabInModal(modal, event) {
  const activeEl = document.activeElement;
  if (!(activeEl instanceof Element)) return false;

  const currentOption = activeEl.closest('.rs-custom-select.open .rs-custom-select__option');
  if (!(currentOption instanceof HTMLElement)) return false;

  const openSelect = currentOption.closest('.rs-custom-select.open');
  if (!(openSelect instanceof HTMLElement) || !modal.contains(openSelect)) return false;

  const tabbable = getOpenCustomSelectTabbableOptions(modal).filter((opt) => openSelect.contains(opt));
  if (!tabbable.length) return false;

  const index = tabbable.indexOf(currentOption);
  const isDisabled = currentOption.classList.contains('rs-custom-select__disabled');

  if (isDisabled || index === -1) {
    const adjacent = getAdjacentTabbableCustomSelectOption(
      openSelect,
      tabbable,
      currentOption,
      !!event.shiftKey
    );
    if (!adjacent) return false;

    event.preventDefault();
    event.stopPropagation();
    syncCustomSelectActiveOption(openSelect, adjacent);
    adjacent.focus({ preventScroll: true });
    return true;
  }

  if (!event.shiftKey && index < tabbable.length - 1) {
    event.preventDefault();
    event.stopPropagation();
    const next = tabbable[index + 1];
    syncCustomSelectActiveOption(openSelect, next);
    next.focus({ preventScroll: true });
    return true;
  }

  if (event.shiftKey && index > 0) {
    event.preventDefault();
    event.stopPropagation();
    const prev = tabbable[index - 1];
    syncCustomSelectActiveOption(openSelect, prev);
    prev.focus({ preventScroll: true });
    return true;
  }

  return false;
}

/**
 * @param {Node} target
 * @param {HTMLElement} modal
 */
function exportModalClickTargetIsFocusable(target, modal) {
  if (!(target instanceof Element)) return false;
  if (target.closest('label[for]')) return true;
  const focusable = target.closest(FOCUSABLE_TARGET_SELECTOR);
  return focusable instanceof HTMLElement && modal.contains(focusable);
}

function refocusExportModal() {
  if (!exportModalTrapState) return;

  const { modal, initialFocusEl, lastFocused, modalContent } = exportModalTrapState;
  if (modal.classList.contains('rs-hidden')) return;

  const focusables = getExportModalFocusables(modal);
  const fallback =
    (lastFocused && modal.contains(lastFocused) ? lastFocused : null) ||
    initialFocusEl ||
    focusables[0] ||
    modalContent ||
    modal;

  fallback.focus({ preventScroll: true });
}

/**
 * Dedicated trap for export modals — independent of trapFocus/removeTrapFocus so other
 * overlays cannot tear it down when the user clicks inside the dialog.
 * @param {HTMLElement} modal
 * @param {{ initialFocus?: HTMLElement | null }} [options]
 */
export function trapExportModalFocus(modal, options = {}) {
  releaseExportModalFocusTrap();

  const initialFocusEl =
    options.initialFocus instanceof HTMLElement && modal.contains(options.initialFocus)
      ? options.initialFocus
      : null;
  const modalContent = modal.querySelector('.rs-modal__content');

  exportModalTrapState = {
    modal,
    initialFocusEl,
    lastFocused: initialFocusEl,
    modalContent: modalContent instanceof HTMLElement ? modalContent : null,
  };

  exportModalInertedElements = inertBackgroundExcept(modal);

  exportModalTrapHandlers.mousedown = (event) => {
    if (!exportModalTrapState || exportModalTrapState.modal !== modal) return;
    if (modal.classList.contains('rs-hidden')) return;
    if (!(event.target instanceof Node) || !modal.contains(event.target)) return;
    if (exportModalClickTargetIsFocusable(event.target, modal)) return;

    event.preventDefault();
    refocusExportModal();
    queueMicrotask(refocusExportModal);
    requestAnimationFrame(refocusExportModal);
  };

  exportModalTrapHandlers.pointerdown = exportModalTrapHandlers.mousedown;

  exportModalTrapHandlers.focusin = (event) => {
    if (!exportModalTrapState || exportModalTrapState.modal !== modal) return;
    const target = event.target;
    if (!(target instanceof Node)) return;

    if (modal.contains(target)) {
      const focusables = getExportModalFocusables(modal);
      const match = focusables.find(
        (el) => el === target || (target instanceof HTMLElement && el.contains(target))
      );
      if (match) exportModalTrapState.lastFocused = match;
      return;
    }

    refocusExportModal();
  };

  exportModalTrapHandlers.focusout = (event) => {
    if (!exportModalTrapState || exportModalTrapState.modal !== modal) return;
    if (!(event.target instanceof Node) || !modal.contains(event.target)) return;

    const related = event.relatedTarget;
    if (related instanceof Node && modal.contains(related)) return;

    queueMicrotask(() => {
      if (!exportModalTrapState || exportModalTrapState.modal !== modal) return;
      if (!modal.contains(document.activeElement)) refocusExportModal();
    });
  };

  exportModalTrapHandlers.keydown = (event) => {
    if (!exportModalTrapState || exportModalTrapState.modal !== modal) return;
    if (modal.classList.contains('rs-hidden')) return;
    if (event.key !== 'Tab') return;

    if (handleOpenCustomSelectTabInModal(modal, event)) return;

    event.preventDefault();
    event.stopPropagation();

    const focusables = getExportModalFocusables(modal);
    if (!focusables.length) {
      refocusExportModal();
      return;
    }

    const activeEl = document.activeElement;
    let idx = activeFocusableIndex(focusables, activeEl);

    if (idx === -1 || !modal.contains(activeEl)) {
      (event.shiftKey ? focusables[focusables.length - 1] : focusables[0])?.focus({
        preventScroll: true,
      });
      return;
    }

    const len = focusables.length;
    const nextFocusable = event.shiftKey
      ? focusables[idx === 0 ? len - 1 : idx - 1]
      : focusables[idx === len - 1 ? 0 : idx + 1];
    nextFocusable?.focus({ preventScroll: true });

    if (nextFocusable instanceof HTMLElement && isFocusableOpenCustomSelectOption(nextFocusable)) {
      const openSelect = nextFocusable.closest('.rs-custom-select.open');
      if (openSelect instanceof HTMLElement) {
        syncCustomSelectActiveOption(openSelect, nextFocusable);
      }
    }
  };

  document.addEventListener('mousedown', exportModalTrapHandlers.mousedown, true);
  document.addEventListener('pointerdown', exportModalTrapHandlers.pointerdown, true);
  document.addEventListener('focusin', exportModalTrapHandlers.focusin, true);
  document.addEventListener('keydown', exportModalTrapHandlers.keydown, true);
  modal.addEventListener('focusout', exportModalTrapHandlers.focusout, true);

  const focusables = getExportModalFocusables(modal);
  const openTarget = initialFocusEl || focusables[0] || modalContent || modal;
  openTarget.focus({ preventScroll: true });
}

/** Release the export-modal-only focus trap. */
export function releaseExportModalFocusTrap() {
  if (!exportModalTrapState) return;

  const { modal } = exportModalTrapState;

  exportModalTrapHandlers.mousedown &&
    document.removeEventListener('mousedown', exportModalTrapHandlers.mousedown, true);
  exportModalTrapHandlers.pointerdown &&
    document.removeEventListener('pointerdown', exportModalTrapHandlers.pointerdown, true);
  exportModalTrapHandlers.focusin &&
    document.removeEventListener('focusin', exportModalTrapHandlers.focusin, true);
  exportModalTrapHandlers.keydown &&
    document.removeEventListener('keydown', exportModalTrapHandlers.keydown, true);
  exportModalTrapHandlers.focusout &&
    modal.removeEventListener('focusout', exportModalTrapHandlers.focusout, true);

  exportModalInertedElements.forEach((el) => el.removeAttribute('inert'));
  exportModalInertedElements = [];
  exportModalTrapState = null;
}

const BANNER_ROOT_SELECTOR =
  '.cart-page-actions__items-removed-banner, .cart-page-actions__prev-date-banner, .cart-page-actions__ship-complete-banner, .rs-product-status-banner, .sales-rep-banner, .rs-add-to-list-toast-notification, .rs-now-viewing-popup, .rs-banner';

/** @type {WeakMap<HTMLElement, boolean>} */
const bannerActiveState = new WeakMap();

/**
 * @param {Element | null | undefined} el
 * @returns {HTMLElement | null}
 */
function resolveBannerRoot(el) {
  if (!(el instanceof Element)) return null;

  const specific = el.closest(
    '.cart-page-actions__items-removed-banner, .cart-page-actions__prev-date-banner, .cart-page-actions__ship-complete-banner, .rs-product-status-banner, .sales-rep-banner, .rs-add-to-list-toast-notification, .rs-now-viewing-popup'
  );
  if (specific instanceof HTMLElement) return specific;

  const rsBanner = el.closest('.rs-banner');
  if (rsBanner instanceof HTMLElement) {
    const itemsRemovedWrapper = rsBanner.closest('.cart-page-actions__items-removed-banner');
    if (itemsRemovedWrapper instanceof HTMLElement && rsBanner !== itemsRemovedWrapper) {
      return itemsRemovedWrapper;
    }
    return rsBanner;
  }

  return null;
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isElementVisible(el) {
  if (el.hasAttribute('hidden')) return false;
  if (el.classList.contains('rs-hidden')) return false;

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  return true;
}

/**
 * @param {HTMLElement} banner
 * @returns {boolean}
 */
function isBannerActive(banner) {
  if (!isElementVisible(banner)) return false;

  if (banner.classList.contains('cart-page-actions__ship-complete-banner')) {
    return banner.classList.contains('is-active');
  }

  if (banner.getAttribute('data-pricebook-dynamic-enabled') === 'true') {
    return banner.classList.contains('rs-banner--pricebook-dynamic');
  }

  if (banner.classList.contains('rs-product-status-banner')) {
    return (
      banner.classList.contains('text--discontinued-visible') ||
      banner.classList.contains('text--phase-out-visible')
    );
  }

  if (
    banner.classList.contains('cart-page-actions__prev-date-banner') ||
    banner.classList.contains('cart-page-actions__items-removed-banner') ||
    banner.classList.contains('product-list-toast') ||
    banner.classList.contains('rs-banner--warranty-returns') ||
    banner.closest('.rs-product-list-detail__notifications')
  ) {
    return banner.classList.contains('rs-banner--visible');
  }

  if (banner.classList.contains('rs-banner') && !banner.classList.contains('rs-banner--visible')) {
    const style = window.getComputedStyle(banner);
    if (style.display === 'none') return false;
  }

  return true;
}

/**
 * Move keyboard focus into an active banner (close button, link, or the banner root).
 * @param {HTMLElement} banner
 */
export function focusBanner(banner) {
  if (!(banner instanceof HTMLElement) || !isBannerActive(banner)) return;

  requestAnimationFrame(() => {
    if (!isBannerActive(banner)) return;

    const focusable = getVisibleFocusableElements(banner);
    const target = focusable[0] || banner;

    if (target === banner && !banner.hasAttribute('tabindex')) {
      banner.setAttribute('tabindex', '-1');
    }

    target.focus({ preventScroll: true });
  });
}

/**
 * @param {HTMLElement} banner
 */
function handleBannerActivation(banner) {
  const wasActive = bannerActiveState.get(banner) ?? false;
  const isActive = isBannerActive(banner);
  bannerActiveState.set(banner, isActive);

  if (isActive && !wasActive) {
    focusBanner(banner);
  }
}

/**
 * Seed banner visibility state and watch for banners becoming active.
 */
export function initBannerFocusManager() {
  document.querySelectorAll(BANNER_ROOT_SELECTOR).forEach((node) => {
    const banner = resolveBannerRoot(node) || (node instanceof HTMLElement ? node : null);
    if (banner instanceof HTMLElement) {
      bannerActiveState.set(banner, isBannerActive(banner));
    }
  });

  const observer = new MutationObserver((mutations) => {
    /** @type {Set<HTMLElement>} */
    const banners = new Set();

    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        const banner = resolveBannerRoot(mutation.target);
        if (banner) banners.add(banner);
        continue;
      }

      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          const banner = resolveBannerRoot(node) || (node.matches(BANNER_ROOT_SELECTOR) ? node : null);
          if (banner instanceof HTMLElement) banners.add(banner);
          node.querySelectorAll(BANNER_ROOT_SELECTOR).forEach((el) => {
            const resolved = resolveBannerRoot(el);
            if (resolved) banners.add(resolved);
          });
        });
      }
    }

    banners.forEach(handleBannerActivation);
  });

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'hidden'],
  });
}

if (typeof window !== 'undefined') {
  window.RSFocus = window.RSFocus || {};
  window.RSFocus.trapFocus = trapFocus;
  window.RSFocus.removeTrapFocus = removeTrapFocus;
  window.RSFocus.inertBackgroundExcept = inertBackgroundExcept;
  window.RSFocus.trapExportModalFocus = trapExportModalFocus;
  window.RSFocus.releaseExportModalFocusTrap = releaseExportModalFocusTrap;
  window.RSFocus.focusBanner = focusBanner;
  window.RSFocus.initBannerFocusManager = initBannerFocusManager;
}
